
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useDataEngine } from '@dhis2/app-runtime';
import { Button, Chip, colors } from '@dhis2/ui';
import { ArrowLeft, Printer, FileDown, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { POSITION_ATTRIBUTES } from '../auth/utils/reportingStructure';
import { useCurrentUserPosition } from '../appraisal/hooks/useCurrentUserPosition';
import { exportAppraisalPDF } from './exportPDF';

const shellStyles = {
  page: {
    maxWidth: '1400px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  hero: {
    background: 'linear-gradient(135deg, #0f5132 0%, #1d8348 58%, #f4d03f 100%)',
    color: '#fff',
    borderRadius: '18px',
    padding: '24px',
    boxShadow: '0 18px 40px rgba(10, 63, 37, 0.18)',
  },
  heroTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    flexWrap: 'wrap',
  },
  heroTitle: {
    margin: 0,
    fontSize: '1.2rem',
    lineHeight: 1.1,
  },
  heroText: {
    margin: '8px 0 0',
    maxWidth: '780px',
    color: 'rgba(255,255,255,0.9)',
  },
  toolbar: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    alignItems: 'center',
    padding: '16px 18px',
    backgroundColor: '#fff',
    border: `1px solid ${colors.grey300}`,
    borderRadius: '14px',
    boxShadow: '0 8px 22px rgba(17, 24, 39, 0.06)',
  },
  content: {
    backgroundColor: '#fff',
    borderRadius: '18px',
    padding: '24px',
    border: `1px solid ${colors.grey300}`,
    boxShadow: '0 12px 30px rgba(17, 24, 39, 0.08)',
  },
};

 const printStyles = `
  @page {
    margin: 0 !important;
    size: A4 portrait;
  }

  @media print {
    /* ============================================ */
    /* HIDE ALL UI ELEMENTS (Buttons, Toolbar, Hero) */
    /* ============================================ */
    
    /* Hide "Back to reports" button container */
    .report-shell__hero-actions {
      display: none !important;
      height: 0 !important;
      width: 0 !important;
      max-height: 0 !important;
      overflow: hidden !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
      visibility: hidden !important;
    }
    
    /* Hide toolbar with Download button */
    .report-shell__toolbar {
      display: none !important;
      height: 0 !important;
      width: 0 !important;
      max-height: 0 !important;
      overflow: hidden !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
      visibility: hidden !important;
    }
    
    /* Hide "Generating..." loading indicator */
    #pdf-loading {
      display: none !important;
      height: 0 !important;
      width: 0 !important;
      max-height: 0 !important;
      overflow: hidden !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
      visibility: hidden !important;
    }
    
    /* Hide hero section (title and description) */
    .report-shell__hero {
      display: none !important;
      height: 0 !important;
      width: 0 !important;
      max-height: 0 !important;
      overflow: hidden !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
      visibility: hidden !important;
      background: none !important;
      box-shadow: none !important;
    }
    
    .report-shell__hero h1 {
      display: none !important;
      height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    
    .report-shell__hero > div {
      display: none !important;
      padding: 0 !important;
      margin: 0 !important;
    }
    
    .report-shell__hero p {
      display: none !important;
      height: 0 !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    /* ============================================ */
    /* COMPANY HEADER - KEEP VISIBLE WITH LOGO */
    /* ============================================ */
    .report-shell__company-header {
      display: flex !important;
      flex-direction: column !important;
      align-items: center !important;
      text-align: center !important;
      margin: 0 0 8px 0 !important;
      padding: 10px 0 !important;
      border-bottom: 2px solid #d9dee5 !important;
      width: 100% !important;
      box-sizing: border-box !important;
      background-color: #ffffff !important;
      visibility: visible !important;
    }

    .report-shell__company-logo {
      max-height: 80px !important;
      max-width: 150px !important;
      width: auto !important;
      height: auto !important;
      margin: 0 0 6px 0 !important;
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
      print-color-adjust: exact !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      filter: none !important;
      -webkit-filter: none !important;
    }

    .report-shell__company-name {
      font-size: 20px !important;
      font-weight: 700 !important;
      color: #0f5132 !important;
      margin: 0 !important;
      text-align: center !important;
      visibility: visible !important;
    }

    .report-shell__company-divider {
      display: none !important;
    }

    /* ============================================ */
    /* PAGE SETUP - FULL WIDTH */
    /* ============================================ */
    @page {
      margin: 0 !important;
      size: A4 portrait;
    }

    html,
    body {
      margin: 0 !important;
      padding: 0 !important;
      width: 100% !important;
    }

    .report-shell__page {
      padding: 0 !important;
      margin: 0 !important;
      gap: 0 !important;
      width: 100% !important;
      max-width: 100% !important;
      overflow: visible !important;
    }

    .report-shell__content {
      padding: 8px 12px !important;
      margin: 0 !important;
      width: 100% !important;
      max-width: 100% !important;
      box-sizing: border-box !important;
      overflow: visible !important;
    }

    .apr-page {
      padding: 0 !important;
      margin: 0 !important;
    }

    /* ============================================ */
    /* TEXT WRAPPING - PREVENT CUT-OFF */
    /* ============================================ */
    .report-shell__content * {
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
      white-space: normal !important;
      max-width: 100% !important;
    }

    .apr-obj-name,
    .apr-obj-panel,
    .objective-details,
    .description-text,
    .alignment-info,
    .apr-obj-panel > div,
    .apr-obj-panel > div > div {
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
      white-space: normal !important;
      max-width: 100% !important;
      box-sizing: border-box !important;
    }

    .kra-item,
    .kpi-item,
    .apr-obj-panel > div > div:last-child > div {
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
      white-space: normal !important;
      max-width: 100% !important;
      box-sizing: border-box !important;
    }

    .apr-obj-panel > div > div:last-child > div > div {
      flex-wrap: wrap !important;
    }

    .apr-obj-panel > div > div:last-child > div > div > span:first-child {
      white-space: normal !important;
      word-wrap: break-word !important;
    }

    .apr-obj-row {
      display: block !important;
      width: 100% !important;
    }

    .apr-obj-row > div {
      width: 100% !important;
      max-width: 100% !important;
      box-sizing: border-box !important;
    }

    .apr-progress-wrap {
      max-width: 100% !important;
      box-sizing: border-box !important;
    }

    /* ============================================ */
    /* HIDE OTHER UI ELEMENTS */
    /* ============================================ */
    .hide-in-print,
    .apr-actions-bar,
    .apr-status-footer,
    .apr-stepper,
    .apr-obj-header,
    .apr-context-banner,
    .apr-selector-bar,
    .apr-status-banner,
    .apr-total-banner,
    .apr-subsection-divider,
    .apr-comments-section,
    .apr-saving-indicator,
    .apr-score-header-row,
    .apr-staff-header > div:last-child,
    .apr-staff-header .apr-chip,
    .report-shell__hero .apr-chip,
    .obj-manual-review,
    [class*="obj-manual-review"] {
      display: none !important;
      height: 0 !important;
      width: 0 !important;
      max-height: 0 !important;
      overflow: hidden !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
    }

    /* Hide progress bars */
    .apr-progress-wrap,
    .apr-progress-track,
    .apr-progress-fill,
    .apr-progress-pct,
    [class*="progress"] {
      display: none !important;
      height: 0 !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    .apr-chip {
      display: none !important;
      height: 0 !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    .apr-obj-row > div:last-child {
      display: none !important;
      height: 0 !important;
      width: 0 !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    /* ============================================ */
    /* STAFF HEADER - COMPACT */
    /* ============================================ */
    .apr-staff-header {
      padding: 0 !important;
      margin: 0 !important;
      border-bottom: 1px solid #ddd !important;
      background: none !important;
      border-radius: 0 !important;
      display: flex !important;
      flex-wrap: wrap !important;
      gap: 0 8px !important;
      height: auto !important;
      min-height: 0 !important;
    }

    .apr-staff-header > div:first-child {
      display: flex !important;
      flex-wrap: wrap !important;
      gap: 0 8px !important;
      align-items: center !important;
      width: 100% !important;
      padding: 2px 0 !important;
      margin: 0 !important;
    }

    .apr-staff-header > div:first-child > div:first-child {
      font-size: 0.8rem !important;
      font-weight: 700 !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    .apr-staff-header > div:first-child > div:last-child {
      display: flex !important;
      flex-wrap: wrap !important;
      gap: 0 8px !important;
      font-size: 0.65rem !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    .apr-staff-header > div:last-child {
      display: none !important;
      height: 0 !important;
      width: 0 !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    /* ============================================ */
    /* OBJECTIVE CARDS - COMPACT */
    /* ============================================ */
    .apr-objective-card {
      margin: 0 !important;
      padding: 0 !important;
      border: none !important;
      border-bottom: 1px solid #e0e0e0 !important;
      border-radius: 0 !important;
      page-break-inside: avoid !important;
    }

    .apr-objective-card:first-child {
      border-top: 1px solid #e0e0e0 !important;
    }

    .apr-obj-row {
      display: flex !important;
      flex-direction: column !important;
      padding: 2px 4px !important;
      cursor: default !important;
      border-bottom: none !important;
    }

    .apr-obj-row > div:not(:first-child) {
      display: none !important;
      height: 0 !important;
      width: 0 !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    .apr-obj-name {
      display: flex !important;
      flex-direction: column !important;
      gap: 1px !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    .apr-obj-name > span:first-child {
      font-size: 0.85rem !important;
      font-weight: 700 !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    .apr-obj-name > span:last-child {
      display: none !important;
      height: 0 !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    .apr-obj-expand-icon {
      display: none !important;
      height: 0 !important;
      width: 0 !important;
    }

    /* ============================================ */
    /* OBJECTIVE PANEL - ALWAYS VISIBLE */
    /* ============================================ */
    .apr-obj-panel {
      display: block !important;
      padding: 2px 4px !important;
      margin: 0 !important;
      max-height: none !important;
      overflow: visible !important;
      border-top: 1px solid #e0e0e0 !important;
    }

    .apr-obj-panel--hidden {
      display: block !important;
    }

    .apr-obj-panel > div {
      display: block !important;
      gap: 0 !important;
    }

    .apr-obj-panel > div > div {
      width: 100% !important;
      max-width: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
    }

    .apr-obj-panel > div > div:first-child {
      display: none !important;
      height: 0 !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    /* ============================================ */
    /* KRA SECTION - COMPACT */
    /* ============================================ */
    .apr-obj-panel > div > div:last-child {
      padding: 0 !important;
      margin: 0 !important;
    }

    .apr-obj-panel-title {
      font-size: 0.65rem !important;
      font-weight: 600 !important;
      margin: 2px 0 !important;
      padding: 0 !important;
    }

    .apr-obj-panel > div > div:last-child > h5 {
      display: none !important;
      height: 0 !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    .apr-obj-panel > div > div:last-child > div {
      margin: 1px 0 !important;
      padding: 2px 4px !important;
      border: 1px solid #eee !important;
      border-radius: 2px !important;
    }

    .apr-obj-panel > div > div:last-child > div > div {
      display: flex !important;
      justify-content: space-between !important;
      align-items: center !important;
      gap: 4px !important;
    }

    .apr-obj-panel > div > div:last-child > div > div > span:first-child {
      font-size: 0.6rem !important;
      font-weight: 600 !important;
    }

    .apr-obj-panel > div > div:last-child > div > div > span:last-child {
      font-size: 0.5rem !important;
      padding: 1px 4px !important;
      border-radius: 8px !important;
    }

    /* ============================================ */
    /* MANUAL SECTION - ONLY FOR TOP-LEVEL USERS */
    /* ============================================ */
    .report-shell__manual-section {
      margin-top: 8px !important;
      padding-top: 6px !important;
      border-top: 2px solid #d9dee5 !important;
      page-break-inside: avoid !important;
    }

    .report-shell__manual-section h3 {
      font-size: 11px !important;
      font-weight: 700 !important;
      color: #0f5132 !important;
      margin: 0 0 2px 0 !important;
    }

    .report-shell__manual-rating-row {
      display: flex !important;
      justify-content: space-between !important;
      padding: 1px 0 !important;
      border-bottom: 1px solid #eee !important;
    }

    .report-shell__manual-rating-row .label {
      font-weight: 600 !important;
      font-size: 9px !important;
    }

    .report-shell__manual-rating-row .value {
      font-size: 9px !important;
      color: #999 !important;
    }

    .report-shell__manual-rating-row .underline {
      border-bottom: 1px solid #999 !important;
      min-width: 30px !important;
      display: inline-block !important;
    }

    .report-shell__manual-signature-row {
      display: flex !important;
      gap: 6px !important;
      margin-top: 4px !important;
    }

    .report-shell__manual-signature-row .field-label {
      font-size: 8px !important;
      color: #666 !important;
      font-weight: 600 !important;
      display: block !important;
      margin-bottom: 0 !important;
    }

    .report-shell__manual-signature-row .field-line {
      border-bottom: 1px solid #ccc !important;
      min-height: 14px !important;
      width: 100% !important;
    }

    .report-shell__manual-comments .field-line {
      border-bottom: 1px solid #ccc !important;
      min-height: 30px !important;
      width: 100% !important;
    }

    .report-shell__manual-rating-grid > div:last-child {
      border-bottom: 2px solid #999 !important;
      padding-top: 2px !important;
      margin-top: 2px !important;
    }

    /* ============================================ */
    /* NUCLEAR RESET - High specificity overrides */
    /* ============================================ */
    .report-shell__content .hide-in-print,
    .report-shell__content *[class*="hide-in-print"] {
      display: none !important;
      height: 0 !important;
      width: 0 !important;
      max-height: 0 !important;
      overflow: hidden !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
    }

    .report-shell__content * {
      margin-top: 0 !important;
      margin-bottom: 0 !important;
      gap: 0 !important;
    }

    .report-shell__content .apr-obj-row {
      display: flex !important;
      flex-direction: column !important;
      padding: 2px 4px !important;
      border-bottom: 1px solid #e0e0e0 !important;
    }

    .report-shell__content .apr-obj-name {
      display: block !important;
      padding: 1px 0 !important;
    }

    .report-shell__content .apr-obj-name > span:first-child {
      display: block !important;
      font-size: 0.85rem !important;
      font-weight: 700 !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    .report-shell__content .apr-obj-name > span:last-child {
      display: none !important;
    }

    .report-shell__content .apr-obj-panel {
      display: block !important;
      padding: 2px 4px !important;
      margin: 0 !important;
      overflow: visible !important;
      border-top: 1px solid #e0e0e0 !important;
    }

    .report-shell__content .apr-obj-panel > div {
      display: block !important;
      gap: 0 !important;
    }

    .apr-obj-panel,
    .apr-obj-panel--hidden {
      display: block !important;
      max-height: none !important;
      overflow: visible !important;
      height: auto !important;
    }

    .apr-obj-panel > div,
    .apr-obj-panel > div > div {
      display: block !important;
      max-height: none !important;
      overflow: visible !important;
    }

    .apr-obj-panel > div > div:last-child,
    .apr-obj-panel > div > div:last-child > div {
      display: block !important;
      max-height: none !important;
      overflow: visible !important;
    }

    .apr-objective-card {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
      display: block !important;
      max-height: none !important;
      overflow: visible !important;
    }

    #appraisal-content {
      height: auto !important;
      max-height: none !important;
      overflow: visible !important;
    }

    .report-shell__content {
      height: auto !important;
      max-height: none !important;
      overflow: visible !important;
    }

    .apr-page {
      height: auto !important;
      max-height: none !important;
      overflow: visible !important;
    }
  }
`;

const spinStyles = `
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  .animate-spin {
    animation: spin 1s linear infinite;
  }
`;


const getAttributeValue = (position, attributeId) =>
  position?.extractedAttributes?.[attributeId] ||
  position?.attributes?.find((attribute) => attribute.attribute === attributeId)?.value ||
  '';

const TOP_LEVEL_ROLES = ['R3lFolCMdoi', 'nLSSLch316I', 'nLSSLch316l'];

const extractAttributes = (attributes = []) => {
  const map = {};
  attributes.forEach((attr) => {
    map[attr.attribute] = attr.value;
  });
  return map;
};

export default function ReportLayout({
  title,
  description,
  badge,
  children,
  hideAutoApproved = true,
  hideSelfRating = true,
  reportId = 'appraisal-content',
  reportTitle = 'appraisal',
  isPrintMode = false,
}) {
  const navigate = useNavigate();
  const engine = useDataEngine();
  const { user } = useAuth();
  const { currentUserPosition, reportingToPosition } = useCurrentUserPosition(
    user?.id,
    user?.organisationUnits || []
  );
  const [approvedByName, setApprovedByName] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  const [companyName, setCompanyName] = useState('');
  const [companyLogo, setCompanyLogo] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyType, setCompanyType] = useState('');

  const preparedByName = useMemo(
    () => user?.displayName || user?.name || user?.username || 'Prepared by',
    [user]
  );

  const isTopLevelByRole = useMemo(
    () => user?.userCredentials?.userRoles?.map(r => r.id).some(roleId => TOP_LEVEL_ROLES.includes(roleId)) || false,
    [user]
  );

  // Add this function inside ReportLayout component
const getLogoDataUrl = useCallback(async (logoUrl) => {
  if (!logoUrl) return null;
  
  try {
    // If it's already a data URL, return it
    if (logoUrl.startsWith('data:')) {
      return logoUrl;
    }
    
    // Fetch the image with credentials
    const response = await fetch(logoUrl, {
      credentials: 'include',
      headers: {
        'Accept': 'image/*'
      }
    });
    
    if (!response.ok) {
      console.warn('Failed to fetch logo:', response.status);
      return logoUrl; // Fallback to original URL
    }
    
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(logoUrl); // Fallback
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('Error converting logo to data URL:', error);
    return logoUrl; // Fallback to original URL
  }
}, []);

// Add state for data URL logo
const [logoDataUrl, setLogoDataUrl] = useState('');

// Add effect to convert logo when it loads
useEffect(() => {
  if (companyLogo) {
    getLogoDataUrl(companyLogo).then(dataUrl => {
      if (dataUrl && dataUrl !== companyLogo) {
        setLogoDataUrl(dataUrl);
        console.log('✅ Logo converted to data URL');
      } else {
        setLogoDataUrl(companyLogo);
      }
    });
  }
}, [companyLogo, getLogoDataUrl]);

  const isTopLevelUser = useMemo(() => {
    if (isTopLevelByRole) return true;
    if (!currentUserPosition?.attributes) return false;
    const attrs = extractAttributes(currentUserPosition.attributes);
    return !attrs['SjP47qi3JeB'];
  }, [currentUserPosition, isTopLevelByRole]);

  const ensureAllContentExpanded = () => {
    const container = document.getElementById(reportId);
    if (!container) return;

    // Find and expand all collapsed panels
    const collapsedPanels = container.querySelectorAll('.apr-obj-panel--hidden');
    collapsedPanels.forEach(panel => {
      panel.classList.remove('apr-obj-panel--hidden');
      panel.style.display = 'block';
      panel.style.maxHeight = 'none';
      panel.style.overflow = 'visible';
    });

    // Find all objective panels and make them visible
    const objPanels = container.querySelectorAll('.apr-obj-panel');
    objPanels.forEach(panel => {
      panel.style.display = 'block';
      panel.style.maxHeight = 'none';
      panel.style.overflow = 'visible';
      panel.style.visibility = 'visible';
      panel.style.opacity = '1';
    });

    // Expand all content within objective panels
    const panelContent = container.querySelectorAll('.apr-obj-panel > div');
    panelContent.forEach(content => {
      content.style.display = 'block';
    });

    // Find all KRA sections and show them
    const kraSections = container.querySelectorAll('[class*="kra"], [class*="KRA"]');
    kraSections.forEach(section => {
      section.style.display = 'block';
      section.style.visibility = 'visible';
      section.style.opacity = '1';
    });

    // Force all objective rows to show their expanded state
    const objRows = container.querySelectorAll('.apr-obj-row');
    objRows.forEach(row => {
      row.setAttribute('data-expanded', 'true');
      
      const expandIcon = row.querySelector('.apr-obj-expand-icon');
      if (expandIcon) {
        try {
          expandIcon.click();
        } catch (e) {
          const panel = row.querySelector('.apr-obj-panel');
          if (panel) {
            panel.style.display = 'block';
            panel.style.maxHeight = 'none';
            panel.style.overflow = 'visible';
          }
        }
      }
    });

    return new Promise(resolve => setTimeout(resolve, 500));
  };

   // Handler for Print button - opens print dialog after generating PDF
const handlePrintPDF = async () => {
  setIsExporting(true);
  try {
    const loadingElement = document.getElementById('pdf-loading');
    if (loadingElement) loadingElement.style.display = 'flex';
    
    await ensureAllContentExpanded();
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const fileName = `${title?.replace(/\s/g, '-') || reportTitle}-report.pdf`;
    
    // Pass true to open print dialog
    await exportAppraisalPDF(reportId, fileName, true);
    
    if (loadingElement) loadingElement.style.display = 'none';
  } catch (error) {
    console.error('Print failed:', error);
    const loadingElement = document.getElementById('pdf-loading');
    if (loadingElement) loadingElement.style.display = 'none';
    alert('Failed to generate print. Please try again.');
  } finally {
    setIsExporting(false);
  }
};

  // In ReportLayout.jsx, update the handleDownloadPDF function:

const handleDownloadPDF = async () => {
  setIsExporting(true);
  try {
    console.log('🔍 Starting PDF download...');
    console.log('🔍 Logo data URL:', logoDataUrl ? 'Present' : 'Missing');
    console.log('🔍 Company logo original:', companyLogo ? 'Present' : 'Missing');

     // ✅ DEBUG: Check if the image exists in the DOM
    const logoImg = document.getElementById('company-logo-img');
    console.log('🔍 Logo image element in DOM:', logoImg);
    if (logoImg) {
      console.log('🔍 Logo image src:', logoImg.src);
      console.log('🔍 Logo image complete:', logoImg.complete);
      console.log('🔍 Logo image naturalWidth:', logoImg.naturalWidth);
      console.log('🔍 Logo image style.display:', logoImg.style.display);
    }
    
    // ✅ DEBUG: Check all images in the DOM
    const allImages = document.querySelectorAll('img');
    console.log(`🔍 Total images in DOM: ${allImages.length}`);
    allImages.forEach((img, i) => {
      console.log(`  Image ${i}: src=${img.src?.substring(0, 50)}..., className=${img.className}`);
    });
    
    const loadingElement = document.getElementById('pdf-loading');
    if (loadingElement) loadingElement.style.display = 'flex';
    
    // ✅ Ensure logo is converted before PDF generation
    if (companyLogo && !logoDataUrl) {
      console.log('⏳ Converting logo to data URL before PDF generation...');
      const dataUrl = await getLogoDataUrl(companyLogo);
      if (dataUrl) {
        setLogoDataUrl(dataUrl);
        console.log('✅ Logo converted before PDF generation');
      }
    }
    
    // ✅ Wait for logo to be rendered in DOM
    await new Promise(resolve => {
      const checkLogo = () => {
        const logoImg = document.getElementById('company-logo-img');
        if (logoImg && logoImg.src && logoImg.complete) {
          console.log('✅ Logo image is loaded and ready');
          resolve();
        } else {
          console.log('⏳ Waiting for logo to load...');
          setTimeout(checkLogo, 200);
        }
      };
      // Check immediately, then every 200ms for up to 5 seconds
      let attempts = 0;
      const maxAttempts = 25;
      const interval = setInterval(() => {
        attempts++;
        const logoImg = document.getElementById('company-logo-img');
        if ((logoImg && logoImg.src && logoImg.complete) || attempts >= maxAttempts) {
          clearInterval(interval);
          if (logoImg && logoImg.src && logoImg.complete) {
            console.log('✅ Logo image is loaded and ready');
          } else {
            console.warn('⚠️ Logo image not loaded after max attempts, proceeding anyway');
          }
          resolve();
        }
      }, 200);
    });
    
    await ensureAllContentExpanded();
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const fileName = `${title?.replace(/\s/g, '-') || reportTitle}-report.pdf`;
    
    // Generate PDF and download
    await exportAppraisalPDF(reportId, fileName, false);
    
    if (loadingElement) loadingElement.style.display = 'none';
  } catch (error) {
    console.error('Download failed:', error);
    const loadingElement = document.getElementById('pdf-loading');
    if (loadingElement) loadingElement.style.display = 'none';
    alert('Failed to download PDF. Please try again.');
  } finally {
    setIsExporting(false);
  }
};

  // Load company data
  useEffect(() => {
    const loadCompanyData = async () => {
      if (!user?.organisationUnits?.[0]?.id) return;

      try {
        const orgUnitId = user.organisationUnits[0].id;

        const orgUnitResponse = await engine.query({
          orgUnit: {
            resource: 'organisationUnits',
            id: orgUnitId,
            params: {
              fields: 'id,name'
            }
          }
        });

        const name = orgUnitResponse.orgUnit?.name || '';
        setCompanyName(name);

        const metadataResponse = await engine.query({
          metadataEvent: {
            resource: 'events',
            params: {
              program: 'NkReudUAW4F',
              programStage: 'htIWgNQWEMm',
              orgUnit: orgUnitId,
              ouMode: 'ACCESSIBLE',
              pageSize: 1,
              order: 'eventDate:desc',
              fields: 'dataValues[dataElement,value]'
            }
          }
        });

        const event = metadataResponse.metadataEvent?.events?.[0];
        let logo = '';
        let email = '';
        let type = '';

        if (event?.dataValues) {
          event.dataValues.forEach(dv => {
            if (dv.dataElement === 'muysgcGfkyN') {
              logo = dv.value || '';
            }
            if (dv.dataElement === 'NkCxPqLeDpq') {
              email = dv.value || '';
            }
            if (dv.dataElement === 'QIMf5a1r1Ot') {
              type = dv.value || '';
            }
          });
        }

        setCompanyLogo(logo);
        setCompanyEmail(email);
        setCompanyType(type);

      } catch (error) {
        console.error('Failed to load company data:', error);
        setCompanyName(user?.organisationUnits?.[0]?.name || '');
      }
    };

    loadCompanyData();
  }, [engine, user]);

  // Load approver data
  useEffect(() => {
    let isActive = true;

    const loadApprover = async () => {
      if (isTopLevelUser) {
        if (isActive) {
          setApprovedByName('');
        }
        return;
      }

      const managerUserId = getAttributeValue(reportingToPosition, POSITION_ATTRIBUTES.CURRENT_INCUMBENT);
      if (!managerUserId) {
        if (isActive) setApprovedByName('');
        return;
      }

      try {
        const response = await engine.query({
          approvedBy: {
            resource: `users/${managerUserId}`,
            params: {
              fields: 'id,displayName,name,username',
            },
          },
        });

        if (isActive) {
          const name = response?.approvedBy?.displayName ||
            response?.approvedBy?.name ||
            response?.approvedBy?.username ||
            '';
          setApprovedByName(name || 'Pending approval');
        }
      } catch (error) {
        if (isActive) setApprovedByName('Pending approval');
      }
    };

    loadApprover();

    return () => {
      isActive = false;
    };
  }, [engine, reportingToPosition, isTopLevelUser]);

  return (
    <>
      <style>{printStyles}</style>
      <style>{spinStyles}</style>
      <div className="report-shell__page" style={shellStyles.page}>
       <div className="report-shell__company-header" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          padding: '8px 0',
          borderBottom: '2px solid #d9dee5',
          marginBottom: '8px',
          width: '100%',
          backgroundColor: '#ffffff',
          boxSizing: 'border-box',
          minHeight: '80px'
        }}>
          <img
            src={logoDataUrl || companyLogo || ''}
            alt={`${companyName} Logo`}
            className="report-shell__company-logo"
            id="company-logo-img"
            style={{
              maxHeight: '80px',      // Increased from 60px
              maxWidth: '150px',      // Increased from 100px
              width: 'auto',
              height: 'auto',
              margin: '0 auto 4px auto',
              display: logoDataUrl || companyLogo ? 'block' : 'none',
              visibility: 'visible',
              opacity: 1
            }}
            onError={(e) => {
              console.warn('Logo failed to load, hiding image');
              e.target.style.display = 'none';
            }}
          />
          <div className="report-shell__company-name" style={{
            fontSize: '20px',        // Increased from 16px
            fontWeight: 700,
            color: '#0f5132'
          }}>
            {companyName || 'Company Name'}
          </div>
          <div className="report-shell__company-divider" />
        </div>

        <section className="report-shell__hero" style={shellStyles.hero}>
          <div style={shellStyles.heroTop}>
            <div>
              {badge && !hideSelfRating && !hideAutoApproved ? (
                <Chip className="hide-in-print" backgroundColor="rgba(255,255,255,0.18)" color="#fff">
                  {badge}
                </Chip>
              ) : null}
              <h1 style={shellStyles.heroTitle}>{title}</h1>
              {description ? <p className="hide-in-print" style={shellStyles.heroText}>{description}</p> : null}
            </div>
            <div
              className="report-shell__hero-actions"
              style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}
            >
              <Button
                small
                onClick={() => navigate('/tasks')}
                icon={<ArrowLeft size={16} />}
              >
                Back to reports
              </Button>
            </div>
          </div>
        </section>

        <section className="report-shell__toolbar" style={shellStyles.toolbar}>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Download button - calls handleDownloadPDF */}
            <Button
              primary
              small
              onClick={handleDownloadPDF}
              icon={isExporting ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />}
              disabled={isExporting}
            >
              {isExporting ? 'Generating...' : 'Download PDF'}
            </Button>
          </div>
        </section>

        {/* Loading indicator */}
        <div
          id="pdf-loading"
          style={{
            display: 'none',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 9999,
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            fontSize: '1.2rem'
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <Loader2 size={40} className="animate-spin" style={{ marginBottom: '16px' }} />
            <div>Generating PDF...</div>
          </div>
        </div>

        <section className="report-shell__content" style={shellStyles.content}>
          {/* Child content - the appraisal form */}
          {children}

          {/* ============================================ */}
          {/* MANUAL RATING & SIGNATURE SECTION - ONLY FOR TOP-LEVEL USERS IN PRINT */}
          {/* ============================================ */}
          {isTopLevelUser && isPrintMode && (
            <div className="report-shell__manual-section">
              <h3>Manager's Evaluation</h3>

              {/* Business Performance Objectives Rating */}
              <div className="report-shell__manual-rating-grid">
                <div className="report-shell__manual-rating-row">
                  <span className="label">Business Performance Objectives (50% Weight):</span>
                  <span className="value">
                    Rating: <span className="underline">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;___</span>
                  </span>
                </div>

                <div className="report-shell__manual-rating-row">
                  <span className="label">Leadership &amp; Behavioural Competencies (50% Weight):</span>
                  <span className="value">
                    Rating: <span className="underline">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;____</span>
                  </span>
                </div>

                <div className="report-shell__manual-rating-row" style={{ borderBottom: '2px solid #999', paddingTop: '12px' }}>
                  <span className="label" style={{ fontWeight: 700 }}>Overall Performance Rating:</span>
                  <span className="value" style={{ fontWeight: 700 }}>
                    <span className="underline">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;____</span>
                  </span>
                </div>
              </div>

              {/* Manager's Comments */}
              <div className="report-shell__manual-comments">
                <span className="field-label">Manager's Comments:</span>
                <div className="field-line" style={{ minHeight: '80px' }} />
              </div>

              {/* Individual Behavioural Ratings */}
              <div style={{ marginTop: '20px' }}>
                <span className="field-label" style={{ fontWeight: 600, fontSize: '14px' }}>Behavioural Competency Ratings:</span>

                <div className="report-shell__manual-rating-row">
                  <span className="label">RESPECT (25%):</span>
                  <span className="value">Rating: <span className="underline">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>____ </span>
                </div>
                <div className="report-shell__manual-rating-row">
                  <span className="label">INTEGRITY (20%):</span>
                  <span className="value">Rating: <span className="underline">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>____ </span>
                </div>
                <div className="report-shell__manual-rating-row">
                  <span className="label">LEARNING (25%):</span>
                  <span className="value">Rating: <span className="underline">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>____ </span>
                </div>
                <div className="report-shell__manual-rating-row">
                  <span className="label">EXCELLENCE (30%):</span>
                  <span className="value">Rating: <span className="underline">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span> ____</span>
                </div>
              </div>

              {/* Signatures */}
              <div className="report-shell__manual-signature-block">
                <div className="report-shell__manual-signature-row">
                  <div className="field">
                    <span className="field-label">Prepared by (Employee):</span>
                    <div className="field-line" style={{ minHeight: '30px' }} />
                    <span style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                      {preparedByName}
                    </span>
                  </div>
                  <div className="field">
                    <span className="field-label">Date:</span>
                    <div className="field-line" style={{ minHeight: '30px' }} />
                  </div>
                </div>

                <div className="report-shell__manual-signature-row" style={{ marginTop: '20px' }}>
                  <div className="field">
                    <span className="field-label">Approved by (Manager):</span>
                    <div className="field-line" style={{ minHeight: '30px' }} />
                  </div>
                  <div className="field">
                    <span className="field-label">Date:</span>
                    <div className="field-line" style={{ minHeight: '30px' }} />
                  </div>
                </div>
              </div>

              {/* Note about manual completion */}
              <div style={{
                marginTop: '20px',
                padding: '12px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                border: '1px solid #e0e0e0',
                fontSize: '12px',
                color: '#666'
              }}>
                <strong>Note:</strong> This form is to be completed manually by the manager.
                All ratings, comments, and signatures should be filled in by hand.
              </div>
            </div>
          )}
        </section>

        <footer className="report-shell__print-footer" style={{ display: 'none' }} />
      </div>
    </>
  );
}